import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import axios from "axios";
import {Status} from "./status";
import {headers} from "./headers";
import {Tag} from "./fields";
import {Character, Object, Shot, Animation} from "./Shot";
import {AnimationType} from "../../routes/common/Animations";
import {deleteLocation, saveLocation} from "./locationsSlice";
import {deleteLayer, saveCharacterLayer, saveLayer, saveLocationLayer, saveObjectLayer} from "./layerSlice";
import {
    unsetAnimation,
    saveAnimation,
    saveCharacterAnimation,
    saveLocationAnimation,
    saveObjectAnimation, deleteAnimation
} from "./animationsSlice";
import {deleteObject, saveObject} from "./objectsSlice";
import {deleteCharacter, saveCharacter} from './charactersSlice';
import {deleteNarration, deleteNarrator, saveNarration, saveNarrator, setNarrator} from "./narrationSlice";
import {deleteChoice, saveChoice} from "./choiceSlice";
import {deleteAction, saveAction, saveShotAction} from "./actionSlice";
import {deleteArgument, saveArgument} from "./argumentSlice";
import {host} from "./api";

export interface ShotState {
    shots: Array<Shot>,
    status: Status,
    shot: Shot | undefined,
    error: string | null | undefined
    newShot: number | undefined
}

const initialState: ShotState = {
    shots: [],
    status: Status.Idle,
    shot: undefined,
    error: null,
    newShot: undefined
}

export const fetchShots = createAsyncThunk('shots', async (params: {projectId: number, sceneId: number}) => {
    const response = await axios.get(`${host}/scenes/${params.sceneId}/shots`, {
        headers: headers(params.projectId?.toString())
    })
    return response.data
})

export const fetchShot = createAsyncThunk('fetchShot', async (params: {projectId: number, shotId: number}) => {
    const response = await axios.get(`${host}/shots/${params.shotId}`, {
        headers: headers(params.projectId?.toString())
    })
    return response.data
})

export const saveShot = createAsyncThunk('saveShot', async (params: { projectId: number, shot: Shot }) => {
    const response = await axios.post(`${host}/shots`,
        params.shot,
        {
            headers: headers(params.projectId?.toString())
        })
    return response.data
})

export const duplicateShot = createAsyncThunk('duplicateShot', async (params: {projectId: number, shotId: number}) => {
    const response = await axios.post(`${host}/shots/duplicate/${params.shotId}`,
        {},
        {
            headers: headers(params.projectId?.toString())
        })
    return response.data
})

export const deleteShot = createAsyncThunk('deleteShot', async (params: { projectId: number, shotId: number }) => {
    const response = await axios.delete(`${host}/shots/${params.shotId}`,
        {
            headers: headers(params.projectId?.toString())
        })
    return response.data
})

export const removeShotTag = createAsyncThunk('removeShotTag', async (params: { projectId: number, shotId: number, tagId: number }) => {
    const response = await axios.delete(`${host}/shots/${params.shotId}/tags/${params.tagId}`,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const addShotTag = createAsyncThunk('addShotTag', async (params: { projectId: number, shotId: number, tag: Tag }) => {
    const response = await axios.post(`${host}/shots/${params.shotId}/tags`,
        params.tag,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const updateCharacter = createAsyncThunk('updateCharacter', async (params: { projectId: number, character: Character }) => {
    const response = await axios.put(`${host}/characters/${params.character.id}`,
        params.character,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const updateObject = createAsyncThunk('updateObject', async (params: { projectId: number, object: Object }) => {
    const response = await axios.put(`${host}/objects/${params.object.id}`,
        params.object,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const setShotTransition = createAsyncThunk('setShotTransition', async (params: { projectId: number, shotId: number, type: AnimationType, transition: Animation }) => {
    const response = await axios.post(`${host}/shots/${params.shotId}/transition/${params.type.toLowerCase()}`,
        params.transition,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const deleteShotTransition = createAsyncThunk('deleteShotTransition', async (params: { projectId: number, shotId: number, type: AnimationType}) => {
    const response = await axios.delete(`${host}/shots/${params.shotId}/transition/${params.type.toLowerCase()}`,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const makeConnection = createAsyncThunk('makeConnection', async (params: { projectId: number, source: number, target: number}) => {
    const response = await axios.post(`${host}/shots/${params.source}/connect/${params.target}`,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const removeConnection = createAsyncThunk('removeConnection', async (params: { projectId: number, source: number, target: number}) => {
    const response = await axios.delete(`${host}/shots/${params.source}/connect/${params.target}`,
        {
            headers: headers(params.projectId.toString())
        })
    return response.data
})

export const shotsSlice = createSlice({
    name: 'shots',
    initialState,
    reducers: {
    },
    extraReducers: function (builder) {
        builder
            .addCase(fetchShots.pending, (state, action) => {
                state.status = Status.Loading
                state.newShot = undefined
                state.shots = []
            })
            .addCase(fetchShots.fulfilled, (state, action) => {
                state.status = Status.Succeeded
                state.shots = action.payload?.sort((a: Shot, b: Shot) => a.id! - b.id! )
            })
            .addCase(fetchShots.rejected, (state, action) => {
                state.status = Status.Failed
                state.error = action.error.message
            })
            .addCase(removeShotTag.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(removeShotTag.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(addShotTag.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(addShotTag.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveShot.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveShot.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(duplicateShot.fulfilled, (state, action) => {
                state.newShot = action.payload.id
            })
            .addCase(fetchShot.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(fetchShot.fulfilled, (state, action) => {
                state.status = Status.Succeeded
                state.shot = action.payload
            })
            .addCase(updateCharacter.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(updateCharacter.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(updateObject.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(updateObject.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(setShotTransition.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(setShotTransition.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteShotTransition.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteShotTransition.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveLocation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveLocation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteLocation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteLocation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveObject.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveObject.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteObject.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteObject.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveNarration.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveNarration.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteNarration.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteNarration.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveCharacter.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveCharacter.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteCharacter.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteCharacter.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveLayer.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveLayer.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteLayer.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteLayer.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(unsetAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(unsetAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveLocationLayer.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveLocationLayer.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveObjectLayer.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveObjectLayer.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveCharacterLayer.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveCharacterLayer.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteNarrator.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteNarrator.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(setNarrator.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(setNarrator.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveNarrator.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveNarrator.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveChoice.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveChoice.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteChoice.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteChoice.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveAction.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveAction.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteAction.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteAction.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveArgument.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveArgument.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteArgument.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteArgument.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveShotAction.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveShotAction.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveCharacterAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveCharacterAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveObjectAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveObjectAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(saveLocationAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(saveLocationAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteAnimation.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteAnimation.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(deleteShot.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(deleteShot.fulfilled, (state, action) => {
                state.status = Status.Idle
            })
            .addCase(makeConnection.pending, (state, action) => {
                state.status = Status.Loading
            })
            .addCase(makeConnection.fulfilled, (state, action) => {
                state.status = Status.Idle
            })

    }
})

export default shotsSlice.reducer;